import React, { useCallback, useEffect, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { useTheme } from "@mui/material/styles";
import { Alert, Button, Chip, Paper, TextField, Typography } from "@mui/material";
import {
  AreaChart,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles()((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  toolbar: {
    display: "flex",
    alignItems: "flex-end",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
  },
  tile: {
    padding: theme.spacing(1.5),
    borderRadius: 10,
    border: `1px solid ${theme.palette.divider}`,
  },
  num: {
    fontSize: "1.4rem",
    fontWeight: 700,
    fontVariantNumeric: "tabular-nums",
  },
  label: {
    fontSize: "0.72rem",
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  chartCard: {
    padding: theme.spacing(1.5),
    borderRadius: 10,
    border: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(2),
    height: 220,
  },
  sectionTitle: {
    fontSize: "0.8rem",
    fontWeight: 700,
    marginBottom: theme.spacing(1),
  },
  detailTable: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "0.8rem",
    "& th": {
      textAlign: "left",
      padding: theme.spacing(0.75, 1),
      fontSize: "0.68rem",
      fontWeight: 700,
      textTransform: "uppercase",
      color: theme.palette.text.secondary,
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    "& td": {
      padding: theme.spacing(0.75, 1),
      borderBottom: `1px solid ${theme.palette.divider}`,
      fontVariantNumeric: "tabular-nums",
    },
  },
  empty: {
    padding: theme.spacing(4),
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
  tileSub: {
    fontSize: "0.68rem",
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.25),
  },
  card: {
    padding: theme.spacing(1.5),
    borderRadius: 10,
    border: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(2),
  },
  cardScroll: {
    maxHeight: 300,
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  modelRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 0),
    fontSize: "0.8rem",
  },
  modelName: {
    width: 220,
    flexShrink: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  modelBarTrack: {
    flex: 1,
    height: 8,
    borderRadius: 4,
    background: theme.palette.action.hover,
    overflow: "hidden",
  },
  modelBarFill: {
    height: "100%",
    borderRadius: 4,
    background: theme.palette.primary.main,
  },
  modelValue: {
    width: 70,
    flexShrink: 0,
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
    fontWeight: 600,
  },
  outcomeRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  twoCol: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(380px, 1fr))",
    gap: theme.spacing(2),
    alignItems: "start",
  },
}));

const OUTCOME_LABELS = {
  hangup: "Colgada",
  socket_error: "Error de conexión",
  no_prompt: "Sin Prompt configurado",
  server_error: "Error del servidor",
  transferred: "Transferida a un asesor",
};
const outcomeLabel = (outcome) => OUTCOME_LABELS[outcome] || outcome;

const formatDuration = (totalSeconds) => {
  const minutes = Math.floor(totalSeconds / 60);
  const hours = Math.floor(minutes / 60);
  if (hours > 0) return `${hours}h ${minutes % 60}m`;
  return `${minutes}m ${totalSeconds % 60}s`;
};

const daysInMonth = (isoDateStr) => {
  const d = new Date(isoDateStr);
  return new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
};

const MONTH_SHORT = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
const formatShortDate = (isoDateStr) => {
  const [, month, day] = isoDateStr.split("-");
  return `${day} ${MONTH_SHORT[Number(month) - 1]}`;
};

const isoDate = (date) => date.toISOString().slice(0, 10);
const startOfMonth = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1);
};

const AiCosts = () => {
  const { classes } = useStyles();
  const theme = useTheme();

  const [dateFrom, setDateFrom] = useState(isoDate(startOfMonth()));
  const [dateTo, setDateTo] = useState(isoDate(new Date()));
  const [draftRange, setDraftRange] = useState({ dateFrom, dateTo });

  const [costs, setCosts] = useState(null);
  const [usage, setUsage] = useState(null);
  const [interactions, setInteractions] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchCosts = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/billing", { params: { dateFrom, dateTo } });
      setCosts(data.costs);
      setUsage(data.usage);
      setInteractions(data.interactions);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    fetchCosts();
  }, [fetchCosts]);

  const applyRange = () => {
    setDateFrom(draftRange.dateFrom);
    setDateTo(draftRange.dateTo);
  };

  const hasDays = Boolean(costs?.days?.length);
  const avgDaily = hasDays ? costs.totalCostUsd / costs.days.length : 0;
  const peakDay = hasDays
    ? costs.days.reduce((max, d) => (d.costUsd > max.costUsd ? d : max), costs.days[0])
    : null;
  const isOngoingMonth = hasDays && dateTo === isoDate(new Date());
  const projectedMonth = isOngoingMonth ? avgDaily * daysInMonth(dateFrom) : null;

  const maxModelCost = costs?.byModel?.length ? costs.byModel[0].costUsd : 0;

  const ticketsAtendidos = interactions?.ticketsAtendidos || 0;
  const costPerTicket = hasDays && ticketsAtendidos > 0 ? costs.totalCostUsd / ticketsAtendidos : null;

  return (
    <MainContainer maxWidth={false}>
      <MainHeader>
        <Title>Costos de IA</Title>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        <div className={classes.toolbar}>
          <TextField
            label="Desde"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={draftRange.dateFrom}
            onChange={(e) => setDraftRange((r) => ({ ...r, dateFrom: e.target.value }))}
          />
          <TextField
            label="Hasta"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={draftRange.dateTo}
            onChange={(e) => setDraftRange((r) => ({ ...r, dateTo: e.target.value }))}
          />
          <Button size="small" variant="contained" onClick={applyRange} sx={{ height: 40 }}>Aplicar</Button>
        </div>

        {loading && !costs && <Typography className={classes.empty}>Cargando...</Typography>}

        {!loading && costs && !costs.configured && (
          <Alert severity="info">
            Todavía no configuraste la Admin API key de OpenAI. Andá a
            Conexiones → tu número de "Llamadas" → pestaña "Cómo activarlo" →
            paso 5, y pegá una Admin key (Settings → Organization → Admin
            keys en OpenAI, con el permiso api.usage.read). Es distinta de
            la llave normal que ya usan los Prompts.
          </Alert>
        )}

        {!loading && costs && costs.configured && (
          <>
            <div className={classes.grid}>
              <div className={classes.tile}>
                <div className={classes.num}>${costs.totalCostUsd.toFixed(2)}</div>
                <div className={classes.label}>Gasto real (toda la cuenta)</div>
                <div className={classes.tileSub}>Incluye todo lo que corre bajo esta cuenta de OpenAI.</div>
              </div>
              {interactions && (
                <div className={classes.tile}>
                  <div className={classes.num}>{ticketsAtendidos}</div>
                  <div className={classes.label}>Clientes atendidos por la IA</div>
                  <div className={classes.tileSub}>{interactions.respuestasIA} respuestas de la IA en este rango.</div>
                </div>
              )}
              {costPerTicket !== null && (
                <div className={classes.tile}>
                  <div className={classes.num}>${costPerTicket.toFixed(3)}</div>
                  <div className={classes.label}>Costo por cliente atendido</div>
                  <div className={classes.tileSub}>Gasto real ÷ clientes atendidos en este rango.</div>
                </div>
              )}
              {hasDays && (
                <div className={classes.tile}>
                  <div className={classes.num}>${avgDaily.toFixed(2)}</div>
                  <div className={classes.label}>Promedio diario</div>
                </div>
              )}
              {peakDay && (
                <div className={classes.tile}>
                  <div className={classes.num}>${peakDay.costUsd.toFixed(2)}</div>
                  <div className={classes.label}>Día de mayor gasto</div>
                  <div className={classes.tileSub}>{peakDay.date}</div>
                </div>
              )}
              {projectedMonth !== null && (
                <div className={classes.tile}>
                  <div className={classes.num}>${projectedMonth.toFixed(2)}</div>
                  <div className={classes.label}>Gasto proyectado del mes</div>
                  <div className={classes.tileSub}>Estimado, siguiendo el ritmo actual -- no es un saldo ni un crédito.</div>
                </div>
              )}
            </div>

            {costs.days.length > 0 ? (
              <>
                <div className={classes.chartCard}>
                  <ResponsiveContainer>
                    <AreaChart data={costs.days} margin={{ top: 10, right: 16, bottom: 0, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="date"
                        stroke={theme.palette.text.secondary}
                        fontSize={11}
                        tickFormatter={formatShortDate}
                      />
                      <YAxis
                        stroke={theme.palette.text.secondary}
                        fontSize={11}
                        tickFormatter={(v) => `$${v}`}
                      />
                      <Tooltip
                        labelFormatter={formatShortDate}
                        formatter={(v) => [`$${Number(v).toFixed(2)}`, "Gasto"]}
                      />
                      <Area
                        type="monotone"
                        dataKey="costUsd"
                        stroke={theme.palette.primary.main}
                        fill={theme.palette.primary.main}
                        fillOpacity={0.15}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>

                <div className={classes.twoCol}>
                  {costs.byModel?.length > 0 && (
                    <div className={classes.card}>
                      <Typography className={classes.sectionTitle}>Por modelo</Typography>
                      <div className={classes.cardScroll}>
                        {costs.byModel.map((m) => (
                          <div className={classes.modelRow} key={m.model}>
                            <span className={classes.modelName} title={m.model}>{m.model}</span>
                            <div className={classes.modelBarTrack}>
                              <div
                                className={classes.modelBarFill}
                                style={{ width: `${maxModelCost ? (m.costUsd / maxModelCost) * 100 : 0}%` }}
                              />
                            </div>
                            <span className={classes.modelValue}>${m.costUsd.toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className={classes.card}>
                    <Typography className={classes.sectionTitle}>Detalle por día</Typography>
                    <div className={classes.cardScroll} style={{ overflowX: "auto" }}>
                      <table className={classes.detailTable}>
                        <thead>
                          <tr>
                            <th>Fecha</th>
                            <th>Gasto (USD)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {costs.days.slice().reverse().map((d) => (
                            <tr key={d.date}>
                              <td>{d.date}</td>
                              <td>${d.costUsd.toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {usage && usage.calls > 0 && (
                  <div className={classes.card} style={{ marginTop: theme.spacing(2) }}>
                    <Typography className={classes.sectionTitle}>Llamadas telefónicas y de voz</Typography>
                    <div className={classes.grid} style={{ marginBottom: 0 }}>
                      <div className={classes.tile}>
                        <div className={classes.num}>{usage.calls}</div>
                        <div className={classes.label}>Llamadas</div>
                      </div>
                      <div className={classes.tile}>
                        <div className={classes.num}>{formatDuration(usage.totalSeconds)}</div>
                        <div className={classes.label}>Tiempo hablado</div>
                      </div>
                      <div className={classes.tile}>
                        <div className={classes.num}>${usage.estimatedCostUsd.toFixed(2)}</div>
                        <div className={classes.label}>Costo estimado</div>
                        <div className={classes.tileSub}>Ya incluido en el gasto real de arriba.</div>
                      </div>
                    </div>
                    <div className={classes.outcomeRow}>
                      {usage.byOutcome.map((o) => (
                        <Chip
                          key={o.outcome}
                          size="small"
                          variant="outlined"
                          label={`${outcomeLabel(o.outcome)}: ${o.calls}`}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <Typography className={classes.empty}>
                Sin gasto registrado en este rango.
              </Typography>
            )}
          </>
        )}
      </Paper>
    </MainContainer>
  );
};

export default AiCosts;
